/**
 * ClassicBulkUploadPrograms.jsx — restored legacy bulk importer.
 *
 * VC/Incubator bulk-uploads an Excel sheet of past/existing programs. Flow:
 *   1. Download template (Excel with 2 example rows).
 *   2. Upload xlsx  → dry-run preview grouped by program.
 *   3. Review counts + errors  → click Commit.
 *   4. Backend creates status='closed' programs, stub startups, applications
 *      and emails every founder an invite.
 */
import React, { useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  X, UploadSimple, FileArrowDown, Warning, CheckCircle, FolderOpen, Users, ArrowRight,
} from '@phosphor-icons/react';

const ClassicBulkUploadPrograms = ({ onClose, onDone }) => {
  const [step, setStep] = useState('upload'); // upload | preview | done
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [skipErrorRows, setSkipErrorRows] = useState(true);

  const downloadTemplate = async () => {
    try {
      const r = await api.get('/vc/programs/classic-bulk-upload/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'founderboard-programs-template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download template');
    }
  };

  const doUpload = async () => {
    if (!file) { toast.error('Pick an Excel file first'); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/vc/programs/classic-bulk-upload/preview', fd);
      setPreview(r.data);
      setStep('preview');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to parse file');
    } finally { setBusy(false); }
  };

  const doCommit = async () => {
    if (!preview?.preview_id) return;
    setBusy(true);
    try {
      const r = await api.post('/vc/programs/classic-bulk-upload/commit', {
        preview_id: preview.preview_id,
        skip_error_rows: skipErrorRows,
      });
      setCommitResult(r.data);
      setStep('done');
      toast.success(`Imported ${r.data.programs_created} program${r.data.programs_created === 1 ? '' : 's'}`);
      if (onDone) onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-10 pb-10 p-4 overflow-y-auto" onClick={onClose} data-testid="classic-bulk-upload-modal">
      <div className="bg-surface border border-border rounded-2xl w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-border">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-primary mb-1">Bulk Upload</div>
            <h2 className="text-2xl font-heading font-bold text-text-primary" style={{ letterSpacing: '-0.02em' }}>Import old programs</h2>
            <p className="text-sm text-text-secondary mt-1">Migrate cohorts you've already run onto Founderboard as closed programs.</p>
          </div>
          <button onClick={onClose} data-testid="classic-bulk-upload-close" className="text-text-muted hover:text-text-primary"><X size={20} weight="bold" /></button>
        </div>

        {step === 'upload' && (
          <div className="p-6 space-y-5">
            <div className="rounded-xl border border-border bg-background p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-text-primary">Step 1 — Download the template</div>
                  <p className="text-xs text-text-secondary mt-1">Excel file with all required columns + 2 example rows. Repeat the program-level columns on every startup row.</p>
                </div>
                <button onClick={downloadTemplate} data-testid="classic-bulk-upload-download-template"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border border-border hover:border-primary hover:text-primary flex-shrink-0">
                  <FileArrowDown size={12} weight="bold" /> Download template
                </button>
              </div>
            </div>

            <div className="rounded-xl border border-border bg-background p-4">
              <div className="text-sm font-semibold text-text-primary mb-3">Step 2 — Upload your filled sheet</div>
              <label className={`block cursor-pointer border-2 border-dashed rounded-xl p-8 text-center transition-colors ${file ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}>
                <input type="file" accept=".xlsx,.xlsm" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" data-testid="classic-bulk-upload-file" />
                <UploadSimple size={28} weight="bold" className={`mx-auto mb-2 ${file ? 'text-primary' : 'text-text-muted'}`} />
                {file ? (
                  <div>
                    <div className="text-sm font-semibold text-text-primary">{file.name}</div>
                    <div className="text-[11px] text-text-muted">{(file.size / 1024).toFixed(1)} KB &middot; click to change</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-sm font-semibold text-text-primary">Click to pick an .xlsx file</div>
                    <div className="text-[11px] text-text-muted mt-1">Max 10 MB &middot; must match the template columns</div>
                  </div>
                )}
              </label>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={onClose} data-testid="classic-bulk-upload-cancel" className="px-4 py-2.5 text-sm font-medium border border-border rounded-lg">Cancel</button>
              <button onClick={doUpload} disabled={!file || busy} data-testid="classic-bulk-upload-parse"
                className="px-5 py-2.5 text-sm font-semibold bg-primary text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? 'Parsing…' : <>Preview import <ArrowRight size={11} weight="bold" /></>}
              </button>
            </div>
          </div>
        )}

        {step === 'preview' && preview && (
          <div className="p-6 space-y-4">
            {preview.file_errors?.length > 0 && (
              <div className="rounded-lg bg-danger/10 border border-danger/40 p-3">
                <div className="text-xs font-semibold text-danger mb-1 inline-flex items-center gap-1"><Warning size={12} weight="bold" /> File issues</div>
                {preview.file_errors.map((e, i) => <div key={i} className="text-[11px] text-danger">{e}</div>)}
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <StatChip label="Programs to create" value={preview.program_count} />
              <StatChip label="Startup rows" value={preview.total_rows} />
              <StatChip label="Rows with errors" value={preview.error_rows} tone={preview.error_rows > 0 ? 'warning' : 'ok'} />
            </div>

            <div className="space-y-3 max-h-[380px] overflow-y-auto pr-1" data-testid="classic-bulk-upload-preview-list">
              {preview.programs.map((p, i) => (
                <div key={i} className="rounded-xl border border-border bg-background p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="text-sm font-semibold text-text-primary">{p.name}</div>
                      <div className="text-[11px] text-text-muted mt-0.5">
                        {p.track ? `${p.track} · ` : ''}{p.cohort_year || '—'} &middot; {p.row_count} startup{p.row_count === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[10px]">
                    {['accepted', 'waitlisted', 'rejected', 'received'].map((s) => (p.by_status?.[s] > 0 ? (
                      <span key={s} className={`px-2 py-0.5 rounded font-semibold ${
                        s === 'accepted' ? 'bg-success/15 text-success' :
                        s === 'rejected' ? 'bg-danger/15 text-danger' :
                        s === 'waitlisted' ? 'bg-warning/15 text-warning' : 'bg-text-muted/15 text-text-muted'
                      }`}>{p.by_status[s]} {s}</span>
                    ) : null))}
                  </div>
                  {p.errors?.length > 0 && (
                    <div className="mt-2 text-[11px] text-danger space-y-0.5">
                      {p.errors.slice(0, 5).map((e, ei) => <div key={ei}>{e}</div>)}
                      {p.errors.length > 5 && <div className="text-text-muted">…and {p.errors.length - 5} more</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <label className="flex items-center gap-2 text-xs text-text-secondary">
              <input type="checkbox" checked={skipErrorRows} onChange={(e) => setSkipErrorRows(e.target.checked)} data-testid="classic-bulk-upload-skip-errors" />
              Skip rows with validation errors ({preview.error_rows} row{preview.error_rows === 1 ? '' : 's'})
            </label>

            <div className="flex justify-between gap-2">
              <button onClick={() => { setStep('upload'); setPreview(null); }} data-testid="classic-bulk-upload-back" className="px-4 py-2.5 text-sm font-medium border border-border rounded-lg">← Back</button>
              <button onClick={doCommit} disabled={busy || preview.program_count === 0} data-testid="classic-bulk-upload-commit"
                className="px-5 py-2.5 text-sm font-semibold bg-primary text-white rounded-lg disabled:opacity-50 inline-flex items-center gap-1.5">
                {busy ? 'Importing…' : `Import ${preview.program_count} program${preview.program_count === 1 ? '' : 's'}`}
              </button>
            </div>
          </div>
        )}

        {step === 'done' && commitResult && (
          <div className="p-6 space-y-4" data-testid="classic-bulk-upload-done">
            <div className="text-center py-4">
              <CheckCircle size={40} weight="fill" className="text-success mx-auto mb-2" />
              <h3 className="text-lg font-heading font-bold text-text-primary">Import complete</h3>
              <p className="text-sm text-text-secondary mt-1">Every founder was emailed an invite — new founders also got login details for the account we created for them.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <StatChip label="Programs created" icon={FolderOpen} value={commitResult.programs_created} />
              <StatChip label="Applications imported" icon={Users} value={commitResult.applications_created} />
              <StatChip label="Stub startups created" value={commitResult.stubs_created} />
              <StatChip label="Logins created" value={commitResult.accounts_created} tone="ok" />
              <StatChip label="Invite emails sent" value={commitResult.invites_sent} tone="ok" />
            </div>
            <div className="flex justify-end pt-2">
              <button onClick={onClose} data-testid="classic-bulk-upload-finish" className="px-5 py-2.5 text-sm font-semibold bg-primary text-white rounded-lg">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatChip = ({ label, value, icon: Icon, tone = 'neutral' }) => {
  const toneCls = tone === 'warning' ? 'text-warning'
    : tone === 'ok' ? 'text-success'
    : 'text-text-primary';
  return (
    <div className="rounded-lg border border-border bg-background p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-text-muted mb-1 inline-flex items-center gap-1">
        {Icon ? <Icon size={10} weight="bold" /> : null} {label}
      </div>
      <div className={`text-2xl font-heading font-bold ${toneCls}`}>{value}</div>
    </div>
  );
};

export default ClassicBulkUploadPrograms;
